import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CoreTenantSettings, SettingsResponse, SsoConfig, SsoStartResponse } from '@cloudsign/shared';
import type { SsoMode } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { Alert, Button, Card, CheckboxField, ConfirmButton, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { goTo } from './redirect';

const SecuritySettings = CoreTenantSettings.shape.security;
type SecurityValues = ReturnType<typeof SecuritySettings.parse>;
type NumberKey = 'sessionIdleMinutes' | 'passwordExpiryDays' | 'passwordReuseCount' | 'reauthHours';
const NUMBER_FIELDS: { key: NumberKey; max: number }[] = [
  { key: 'passwordExpiryDays', max: 730 },
  { key: 'passwordReuseCount', max: 24 },
  { key: 'reauthHours', max: 720 },
  { key: 'sessionIdleMinutes', max: 1440 },
];
const SETTINGS_KEY = ['settings', '/settings', 'core'];

/** Password rules, two-factor and support access: the `security` section of the core tenant settings. */
function PolicyCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: SETTINGS_KEY, queryFn: () => api.get('/settings/core', { schema: SettingsResponse }) });
  const [form, setForm] = useState<SecurityValues | null>(null);
  const saved = q.data ? SecuritySettings.parse(q.data.values['security'] ?? {}) : null;
  useEffect(() => {
    if (saved && !form) setForm(saved);
  }, [saved, form]);

  const save = useMutation({
    mutationFn: () => {
      const changed: Record<string, unknown> = {};
      if (form && saved) for (const k of Object.keys(form) as (keyof SecurityValues)[]) if (form[k] !== saved[k]) changed[k] = form[k];
      return api.patch('/settings/core', { body: { security: changed }, schema: SettingsResponse });
    },
    onSuccess: (r) => {
      qc.setQueryData(SETTINGS_KEY, r);
      setForm(SecuritySettings.parse(r.values['security'] ?? {}));
      toast(t('common.saved'));
    },
  });

  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!saved || !form) return <Spinner />;
  const dirty = (Object.keys(form) as (keyof SecurityValues)[]).some((k) => form[k] !== saved[k]);
  const errs = fieldErrors(save.error);
  const set = <K extends keyof SecurityValues>(k: K, v: SecurityValues[K]) => setForm({ ...form, [k]: v });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-6">
      <Card
        title={t('security.policy.title')}
        description={t('security.policy.description')}
        footer={
          <>
            <Button disabled={!dirty} onClick={() => setForm(saved)}>
              {t('settings.discard')}
            </Button>
            <Button type="submit" variant="primary" disabled={!dirty || save.isPending}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="max-w-xl space-y-4">
          <ErrorLine error={save.error} />
          <CheckboxField
            label={t('security.policy.require2fa')}
            hint={t('security.policy.require2fa.hint')}
            checked={form.require2fa}
            onChange={(e) => set('require2fa', e.target.checked)}
          />
          {NUMBER_FIELDS.map(({ key, max }) => (
            <TextField
              key={key}
              label={t(`security.policy.${key}`)}
              hint={t(`security.policy.${key}.hint`)}
              type="number"
              inputMode="numeric"
              min={0}
              max={max}
              step={1}
              value={form[key]}
              error={errs[`security.${key}`] ?? errs[key]}
              onChange={(e) => set(key, Math.max(0, Math.min(max, Math.trunc(Number(e.target.value) || 0))))}
            />
          ))}
          <h3 className="pt-2 text-sm font-semibold text-slate-900">{t('security.support.title')}</h3>
          <p className="text-sm text-slate-600">{t('security.support.description')}</p>
          <CheckboxField
            label={t('security.support.allow')}
            checked={form.allowSupportAccess}
            onChange={(e) => set('allowSupportAccess', e.target.checked)}
          />
          <CheckboxField
            label={t('security.support.notify')}
            checked={form.supportNotifyAdmins}
            onChange={(e) => set('supportNotifyAdmins', e.target.checked)}
          />
        </div>
      </Card>
    </form>
  );
}

const MODES: SsoMode[] = ['off', 'optional', 'required'];

function SsoCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const result = params.get('sso');
  const key = ['sso', 'config'];
  const q = useQuery({ queryKey: key, queryFn: () => api.get('/sso/config', { schema: SsoConfig }) });
  const [draft, setDraft] = useState<{ mode: SsoMode; domains: string; breakGlassAdmins: boolean; satisfiesTwoFactor: boolean } | null>(null);
  useEffect(() => {
    if (q.data) {
      setDraft({
        mode: q.data.mode,
        domains: q.data.domains.join('\n'),
        breakGlassAdmins: q.data.breakGlassAdmins,
        satisfiesTwoFactor: q.data.satisfiesTwoFactor,
      });
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch('/sso/config', {
        body: {
          mode: draft?.mode,
          domains: parseDomains(draft?.domains ?? ''),
          breakGlassAdmins: draft?.breakGlassAdmins,
          satisfiesTwoFactor: draft?.satisfiesTwoFactor,
        },
        schema: SsoConfig,
      }),
    onSuccess: (r) => {
      qc.setQueryData(key, r);
      toast(t('common.saved'));
    },
  });
  const connect = useMutation({
    mutationFn: () => api.post('/sso/connect', { schema: SsoStartResponse }),
    onSuccess: (r) => goTo(r.url),
  });
  const disconnect = useMutation({
    mutationFn: () => api.post('/sso/disconnect'),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: key });
      toast(t('sso.disconnected'));
    },
  });

  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data || !draft) return <Spinner />;
  const cfg = q.data;
  const connected = cfg.directoryId !== null;
  const dirty =
    draft.mode !== cfg.mode ||
    draft.breakGlassAdmins !== cfg.breakGlassAdmins ||
    draft.satisfiesTwoFactor !== cfg.satisfiesTwoFactor ||
    parseDomains(draft.domains).join('\n') !== cfg.domains.join('\n');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Card
      title={t('sso.title')}
      description={t('sso.description')}
      footer={
        <Button type="submit" form="sso-form" variant="primary" disabled={!dirty || save.isPending}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="max-w-xl space-y-4">
        {result === 'connected' ? <Alert kind="success">{t('sso.connectedNotice')}</Alert> : null}
        {result === 'failed' ? <Alert>{t('sso.failedNotice')}</Alert> : null}
        {result ? (
          <Button variant="ghost" onClick={() => setParams({}, { replace: true })}>
            {t('common.dismiss')}
          </Button>
        ) : null}
        <ErrorLine error={connect.error ?? disconnect.error} />
        <p>
          {connected
            ? t('sso.status.connected', { directory: cfg.directoryId ?? '', date: formatDateTime(cfg.connectedAt) })
            : t('sso.status.notConnected')}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant={connected ? 'secondary' : 'primary'} disabled={connect.isPending} onClick={() => connect.mutate()}>
            {t('sso.connect')}
          </Button>
          {connected ? (
            <ConfirmButton label={t('sso.disconnect')} confirmLabel={t('sso.disconnect')} pending={disconnect.isPending} onConfirm={() => disconnect.mutate()} />
          ) : null}
        </div>
        {!connected ? <p className="text-sm text-slate-600">{t('sso.connect.hint')}</p> : null}
        <p className="text-sm text-slate-600">{t('sso.connect.signInHint')}</p>
        <form id="sso-form" onSubmit={submit} className="space-y-4">
          <ErrorLine error={save.error} />
          <SelectField
            label={t('sso.mode')}
            hint={connected ? t('sso.mode.hint') : t('sso.mode.needsConnect')}
            value={draft.mode}
            disabled={!connected && cfg.mode === 'off'}
            onChange={(e) => setDraft({ ...draft, mode: e.target.value as SsoMode })}
          >
            {MODES.map((m) => (
              <option key={m} value={m}>
                {t(`sso.mode.${m}`)}
              </option>
            ))}
          </SelectField>
          <div>
            <label htmlFor="sso-domains" className="mb-1 block text-sm font-medium text-slate-800">
              {t('sso.domains')}
            </label>
            <textarea
              id="sso-domains"
              rows={3}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              aria-describedby="sso-domains-hint"
              value={draft.domains}
              onChange={(e) => setDraft({ ...draft, domains: e.target.value })}
            />
            <p id="sso-domains-hint" className="mt-1 text-xs text-slate-600">
              {t('sso.domains.hint')}
            </p>
          </div>
          <DomainProofs cfg={cfg} onChange={(r) => qc.setQueryData(key, r)} />
          <CheckboxField
            label={t('sso.breakGlass')}
            hint={t('sso.breakGlass.hint')}
            checked={draft.breakGlassAdmins}
            onChange={(e) => setDraft({ ...draft, breakGlassAdmins: e.target.checked })}
          />
          <CheckboxField
            label={t('sso.satisfiesTwoFactor')}
            hint={t('sso.satisfiesTwoFactor.hint')}
            checked={draft.satisfiesTwoFactor}
            onChange={(e) => setDraft({ ...draft, satisfiesTwoFactor: e.target.checked })}
          />
        </form>
      </div>
    </Card>
  );
}

/** The TXT record each listed domain needs, and a check button (ADR 0028 amendment: only proven domains are used). */
function DomainProofs({ cfg, onChange }: { cfg: SsoConfig; onChange: (r: SsoConfig) => void }) {
  const toast = useToast();
  const check = useMutation({
    mutationFn: (domain: string) => api.post('/sso/domains/verify', { body: { domain }, schema: SsoConfig }),
    onSuccess: (r, domain) => {
      onChange(r);
      if (r.domainStatus?.find((d) => d.domain === domain)?.verifiedAt) toast(t('sso.domains.checked', { domain }));
    },
  });
  const list = cfg.domainStatus ?? [];
  if (list.length === 0) return null;
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">{t('sso.domains.proofHint')}</p>
      <ErrorLine error={check.error} />
      <ul className="space-y-3">
        {list.map((d) => (
          <li key={d.domain} className="rounded-md border border-slate-200 p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium text-slate-800">{d.domain}</span>
              <span className={d.verifiedAt ? 'text-green-700' : 'text-amber-700'}>
                {d.verifiedAt ? t('sso.domains.verifiedStatus') : t('sso.domains.pendingStatus')}
              </span>
            </div>
            {!d.verifiedAt ? (
              <div className="mt-2 space-y-1">
                <p>
                  {t('sso.domains.recordName')}: <code className="break-all">{d.recordName}</code>
                </p>
                <p>
                  {t('sso.domains.recordValue')}: <code className="break-all">{d.recordValue}</code>
                </p>
                <Button variant="secondary" disabled={check.isPending} onClick={() => check.mutate(d.domain)}>
                  {t('sso.domains.check')}
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function parseDomains(text: string): string[] {
  return [...new Set(text.split(/[\s,;]+/).map((d) => d.trim().toLowerCase().replace(/^@/, '')).filter(Boolean))];
}

export function SecurityPage() {
  return (
    <>
      <PageHeader title={t('security.title')} description={t('security.description')} />
      <div className="space-y-6">
        <PolicyCard />
        <SsoCard />
      </div>
    </>
  );
}
